import { useMutation, useQueryClient } from '@tanstack/react-query';

import { stopTimer } from '../api/active-timer.api';
import { ACTIVE_TIMER_QUERY_KEYS } from '../constants/active-timer-query-keys';
import { StopTimerRequest } from '../types/dto/active-timer.dto';
import { WorklogResponse } from '../types/dto/worklogs-summary.dto';
import { invalidateTimeTrackingQueries } from '../utils/time-tracking-query-invalidation';
import { notifyActiveTimerChanged } from './use-active-timer';

export const useStopTimer = () => {
  const queryClient = useQueryClient();

  return useMutation<WorklogResponse | null, Error, StopTimerRequest>({
    mutationFn: values => stopTimer(values),
    // Discard any in-flight GET /worklogs/timer BEFORE mutating. Without this,
    // a stale fetch started earlier (e.g. by the return-to-tab revalidation)
    // can resolve AFTER onSuccess seeds the fresh state and overwrite it with
    // pre-mutation data - the root cause of the "timer flashes and disappears"
    // / diverged timer states after a long-idle tab (OPS-1538 / OPS-1625).
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ACTIVE_TIMER_QUERY_KEYS.all });
    },
    // Stop clears the timer (set to null directly — no redundant GET that would
    // just return null) and refreshes the worklog lists so the new entry shows.
    onSuccess: async () => {
      queryClient.setQueryData(ACTIVE_TIMER_QUERY_KEYS.all, null);
      await invalidateTimeTrackingQueries(queryClient);
    },
    // A failed stop (e.g. expired session after a long-idle tab) must not
    // leave the UI on its stale snapshot: re-sync both the timer and the
    // totals from the server instead of waiting for a manual refresh.
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: ACTIVE_TIMER_QUERY_KEYS.all });
      await invalidateTimeTrackingQueries(queryClient);
    },
    onSettled: () => {
      notifyActiveTimerChanged();
    },
  });
};
